/**
 * AccioChatPane — the operator ↔ Accio orchestration chat, bridged over Telegram.
 * Messaging-app styling: consecutive messages group under one avatar + name + time,
 * day dividers separate sessions, outbound messages show sent/delivered ticks, and
 * a WhatsApp-style typing indicator + blinking cursor cover live streaming (the
 * bridge relays the agent's message edits under a stable stream_id). Adaptive
 * polling: fast while a turn is in flight, slow when idle.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, RefreshCw, Check, CheckCheck } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import Markdown from '../../../components/Markdown';

const fmtTime = (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const dayLabel = (t) => {
  const d = new Date(t); const now = new Date();
  const strip = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((strip(now) - strip(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], {
    month: 'short', day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

const AccioChatPane = ({ tenderId = '' }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [awaiting, setAwaiting] = useState(false); // waiting on the agent's turn
  const endRef = useRef(null);

  const streamingActive = messages.some((m) => m.sender === 'accio' && m.streaming);

  const load = useCallback(async () => {
    try {
      const d = await forgeAPI.getAccioChat(tenderId);
      const msgs = d.messages || [];
      setMessages(msgs);
      const streaming = msgs.some((m) => m.sender === 'accio' && m.streaming);
      const lastIsAccio = msgs.length && msgs[msgs.length - 1].sender === 'accio';
      if (streaming) setAwaiting(true);
      else if (lastIsAccio) setAwaiting(false);
    } catch { /* transient */ }
  }, [tenderId]);

  useEffect(() => {
    load();
    const fast = awaiting || streamingActive;
    const id = setInterval(load, fast ? 1200 : 5000);
    return () => clearInterval(id);
  }, [load, awaiting, streamingActive]);

  const lastBody = messages[messages.length - 1]?.body;
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); },
    [messages.length, lastBody, awaiting]);

  const send = async (text) => {
    const body = (text ?? draft).trim();
    if (!body || busy) return;
    setBusy(true); setErr(null); setDraft('');
    setMessages((prev) => [...prev, { message_id: `local-${Date.now()}`, sender: 'operator', body, ts: new Date().toISOString() }]);
    setAwaiting(true);
    try { await forgeAPI.sendAccioChat(body, tenderId); await load(); }
    catch { setErr('Could not reach the Accio bridge — is it running?'); }
    finally { setBusy(false); }
  };

  const lastAgent = [...messages].reverse().find((m) => m.sender === 'accio');
  const chips = (lastAgent?.followups || []).slice(0, 4);
  const showWorking = awaiting && !streamingActive;
  const online = awaiting || streamingActive;

  // Build the render list: day dividers + messages grouped by sender/time.
  const items = [];
  let lastDay = null, lastSender = null, lastTs = 0;
  messages.forEach((m) => {
    const t = m.ts ? new Date(m.ts).getTime() : Date.now();
    const day = dayLabel(t);
    if (day !== lastDay) { items.push({ kind: 'day', id: `day-${day}-${t}`, label: day }); lastDay = day; lastSender = null; }
    const grouped = m.sender === lastSender && (t - lastTs) < 5 * 60000;
    items.push({ kind: 'msg', m, t, head: !grouped });
    lastSender = m.sender; lastTs = t;
  });

  return (
    <div className="forge-accio">
      <div className="forge-accio-head">
        <span className="forge-accio-hd-av"><Bot size={16} /><span className={`forge-accio-dot ${online ? 'on' : ''}`} /></span>
        <div className="forge-accio-hd-meta">
          <b>Accio</b>
          <span className="forge-accio-hd-status">{online ? 'working…' : 'online'} · sourcing agent · via Telegram</span>
        </div>
        <button className="forge-accio-refresh" onClick={load} title="Refresh"><RefreshCw size={13} /></button>
      </div>

      <div className="forge-accio-log">
        {items.length === 0 && !awaiting && (
          <div className="forge-accio-empty">
            <span className="forge-accio-empty-av"><Bot size={22} /></span>
            No messages yet. Name a tender to source — e.g. <b>“source cb-401-44426012”</b>.
          </div>
        )}

        {items.map((it) => {
          if (it.kind === 'day') return <div key={it.id} className="forge-accio-day"><span>{it.label}</span></div>;
          const { m, t, head } = it;
          const out = m.sender !== 'accio';
          const sending = m.message_id.startsWith('local-');
          return (
            <div key={m.message_id} className={`forge-accio-row ${out ? 'out' : 'in'} ${head ? 'head' : 'cont'}`}>
              {!out && <div className="forge-accio-av">{head && <Bot size={15} />}</div>}
              <div className="forge-accio-col">
                {head && (
                  <div className="forge-accio-meta">
                    <span className="forge-accio-name">{out ? 'You' : 'Accio'}</span>
                    <span className="forge-accio-time">{fmtTime(t)}</span>
                  </div>
                )}
                <div className={`forge-accio-bubble ${out ? 'out' : 'in'} ${head ? 'head' : ''}`}>
                  <Markdown>{m.body || ''}</Markdown>
                  {m.streaming && <span className="forge-accio-cursor" />}
                  {out && !m.streaming && (
                    <span className="forge-accio-tick" title={sending ? 'Sending…' : 'Sent'}>
                      {sending ? <Check size={13} /> : <CheckCheck size={13} />}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {showWorking && (
          <div className="forge-accio-row in head">
            <div className="forge-accio-av"><Bot size={15} /></div>
            <div className="forge-accio-col">
              <div className="forge-accio-bubble in head forge-accio-working">
                <span className="forge-accio-typing"><i /><i /><i /></span>
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {chips.length > 0 && (
        <div className="forge-accio-chips">
          {chips.map((c, i) => (
            <button key={i} className="forge-accio-chip" disabled={busy} onClick={() => send(c)}>{c}</button>
          ))}
        </div>
      )}

      {err && <div className="forge-accio-err">{err}</div>}

      <div className="forge-accio-composer">
        <div className="forge-accio-inputwrap">
          <textarea
            rows={1}
            value={draft}
            placeholder="Message Accio…  (e.g. “source cb-401-44426012”)"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <button className="forge-accio-send" disabled={busy || !draft.trim()} onClick={() => send()} title="Send">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccioChatPane;
